import { cn } from '@forest/shared'
import {
  createEffect,
  createSignal,
  on,
  onCleanup,
  Show,
  untrack,
  type Accessor,
  type JSX,
} from 'solid-js'
import {
  BOOT_SKELETON_FADE_MS,
  REVEAL_HIDDEN_CLASS,
  REVEAL_TRANSITION_CLASS,
} from './boot-skeleton'

/**
 * One part of the page crossfading from its skeleton to its content, the
 * Shop reveal's crossfade at the scale of a region: the Classic reveal's
 * catalogue area (GLOSSARY.md "Classic reveal"). Both layers share one grid
 * cell, so the skeleton holds the content's place; the content is inert,
 * hidden, and collapsed to no height until revealed — a grid of pending
 * slots is far taller than the skeleton, and the page must not scroll into
 * blank space — then the skeleton fades out and unmounts. Mounted
 * already revealed — a later return to the tab — it is just the content.
 */
export function SkeletonCrossfade(props: {
  children: JSX.Element
  revealed: Accessor<boolean>
  skeleton: JSX.Element
  testId: string
}) {
  const [gone, setGone] = createSignal(untrack(() => props.revealed()))
  createEffect(
    on(
      () => props.revealed(),
      (revealed) => {
        if (!revealed || untrack(gone)) return
        const fade = setTimeout(() => setGone(true), BOOT_SKELETON_FADE_MS)
        onCleanup(() => clearTimeout(fade))
      },
    ),
  )

  return (
    <div class="grid">
      <div
        inert={!props.revealed()}
        class={cn(
          '[grid-area:1/1]',
          !gone() && REVEAL_TRANSITION_CLASS,
          !props.revealed() && cn('h-0 overflow-hidden', REVEAL_HIDDEN_CLASS),
        )}
      >
        {props.children}
      </div>
      <Show when={!gone()}>
        <div
          data-testid={props.testId}
          data-state={props.revealed() ? 'leaving' : 'showing'}
          aria-hidden="true"
          class={cn(
            '[grid-area:1/1]',
            REVEAL_TRANSITION_CLASS,
            props.revealed() && cn('pointer-events-none', REVEAL_HIDDEN_CLASS),
          )}
        >
          {props.skeleton}
        </div>
      </Show>
    </div>
  )
}
